import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Image as ImageIcon, Save, X, Loader2 } from "lucide-react";
import { API } from "../Sidebar";

/**
 * Compact per-row image-URL editor for services + industries.
 * - Paste a URL, click Save → PUT /sites/{id}/services|industries/{slug}/image
 * - Clear button sets image_url to ""
 * - Live thumbnail preview on the left
 */
export const ImageManagerCard = ({ siteId, onAnyChange }) => {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState({}); // key = "type:slug" -> draft url
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const r = await axios.get(`${API}/commercial/sites/${siteId}/services-with-images`);
      setData(r.data);
    } catch {
      toast.error("Échec du chargement des images");
    }
  };

  useEffect(() => { if (siteId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [siteId]);

  if (!data) {
    return (
      <Card><CardContent className="p-5 flex items-center gap-2 text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement des images…
      </CardContent></Card>
    );
  }

  const draftKey = (kind, slug) => `${kind}:${slug}`;

  const saveOne = async (kind, slug, value) => {
    const k = draftKey(kind, slug);
    setBusy((b) => ({ ...b, [k]: true }));
    try {
      const endpoint = kind === "service"
        ? `${API}/commercial/sites/${siteId}/services/${slug}/image`
        : `${API}/commercial/sites/${siteId}/industries/${slug}/image`;
      await axios.put(endpoint, { image_url: value || "" });
      toast.success(value ? `Image définie pour ${slug}` : `Image effacée pour ${slug}`);
      setDrafts((d) => { const n = { ...d }; delete n[k]; return n; });
      await load();
      if (onAnyChange) onAnyChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec sauvegarde");
    } finally {
      setBusy((b) => ({ ...b, [k]: false }));
    }
  };

  const Row = ({ kind, item }) => {
    const k = draftKey(kind, item.slug);
    const currentUrl = item.image_url || "";
    const draft = drafts[k];
    const value = draft !== undefined ? draft : currentUrl;
    const hasChanges = draft !== undefined && draft !== currentUrl;

    return (
      <div className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-b-0" data-testid={`img-row-${kind}-${item.slug}`}>
        <div className="w-16 h-9 rounded shrink-0 bg-slate-100 overflow-hidden border border-slate-200 flex items-center justify-center">
          {currentUrl ? (
            <img src={currentUrl} alt="" className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <ImageIcon className="w-4 h-4 text-slate-300" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-slate-700 truncate">{item.name}</div>
          <Input
            value={value}
            placeholder="https://…/image.jpg"
            onChange={(e) => setDrafts((d) => ({ ...d, [k]: e.target.value }))}
            className="h-8 text-xs mt-1 font-mono"
            data-testid={`img-input-${kind}-${item.slug}`}
          />
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {hasChanges && (
            <Button
              size="sm" variant="default" className="h-8 px-2"
              onClick={() => saveOne(kind, item.slug, draft)}
              disabled={busy[k]}
              data-testid={`img-save-${kind}-${item.slug}`}
            >
              {busy[k] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            </Button>
          )}
          {currentUrl && !hasChanges && (
            <Button
              size="sm" variant="ghost" className="h-8 px-2 text-slate-400 hover:text-red-600"
              onClick={() => saveOne(kind, item.slug, "")}
              disabled={busy[k]}
              data-testid={`img-clear-${kind}-${item.slug}`}
              title="Effacer l'image"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card data-testid="image-manager-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <ImageIcon className="w-4 h-4 text-slate-500" />
          <h2 className="text-base font-semibold text-slate-900">Images Homepage (Services & Industries)</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Colle une URL publique d'image (jpg, png, webp). Format optimal : <strong>16:9, 640×360+px</strong>.
          Affichée comme bannière en haut de la carte sur la homepage.
          Si vide, la carte ne montre pas d'image.
        </p>

        <h3 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Services ({data.services.length})</h3>
        <div className="mb-4">
          {data.services.map((s) => <Row key={`svc-${s.slug}`} kind="service" item={s} />)}
        </div>

        <h3 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Industries ({data.industries.length})</h3>
        <div>
          {data.industries.map((i) => <Row key={`ind-${i.slug}`} kind="industry" item={i} />)}
        </div>

        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-4">
          💡 Après modification, republie le <strong>Batch 1</strong> depuis le dashboard pour propager les images sur la homepage live.
        </p>
      </CardContent>
    </Card>
  );
};
